import { api, resolvePagination } from './api';

export const fetchClinicalMeasurementsPage = async ({
    patient,
    type,
    source = 'DOCTOR',
    timestamp,
    pageLimit = 50,
    pageToken,
    signal
}) => {
    if (!patient || !type || !['PATIENT', 'DOCTOR', 'DEVICE'].includes(source))
        throw new Error('Patient, source and measurement type are required.');
    if (
        !Number.isInteger(pageLimit) ||
        pageLimit < 1 ||
        pageLimit > 50 ||
        (timestamp && !Number.isFinite(Date.parse(timestamp)))
    ) {
        throw new Error('Invalid measurement pagination limit or start timestamp.');
    }
    const response = await api.get('/cheart/measurements/v2', {
        headers: {
            ...resolvePagination(pageToken, pageLimit),
            'CHEART-QUERY-PATIENT': patient,
            'CHEART-QUERY-MEASUREMENT-TYPE': JSON.stringify([type]),
            'CHEART-QUERY-MEASUREMENT-SOURCE': source,
            'CHEART-QUERY-ORDER': 'DESC',
            ...(timestamp ? { 'CHEART-QUERY-TIMESTAMP': timestamp } : {})
        },
        ...(signal ? { signal } : {})
    });
    const rawCount = response.headers['pagination-count'];
    const count = Number(rawCount);
    if (
        !Array.isArray(response.data) ||
        rawCount == null ||
        rawCount === '' ||
        !Number.isSafeInteger(count) ||
        count < 0
    ) {
        throw new Error('Invalid measurement response or pagination metadata.');
    }
    if (
        response.data.some(
            (item) =>
                item.source !== source ||
                item.dtype !== type ||
                item.patient?.uuid !== patient ||
                !Number.isFinite(Date.parse(item.ts)) ||
                !item.uuid
        )
    ) {
        throw new Error(
            'Measurement response does not match the requested patient, type or source.'
        );
    }
    return {
        measurements: response.data,
        count,
        token: response.headers['pagination-token'] || null
    };
};

export const fetchClinicalMeasurements = async (options) => {
    const measurements = [];
    const tokens = new Set();
    let pageToken;
    let received = 0;
    let count = 0;
    const end = options.endTimestamp ? Date.parse(options.endTimestamp) : null;
    if (end !== null && !Number.isFinite(end))
        throw new Error('Invalid measurement end timestamp.');
    do {
        const page = await fetchClinicalMeasurementsPage({ ...options, pageToken });
        count = page.count;
        received += page.measurements.length;
        // v2 has no end filter yet; count progress before excluding newer records.
        measurements.push(
            ...page.measurements.filter(
                (item) => end === null || Date.parse(item.ts) < end
            )
        );
        if (received >= count) break;
        if (!page.measurements.length || !page.token || tokens.has(page.token)) {
            throw new Error('Incomplete measurement pagination.');
        }
        tokens.add(page.token);
        pageToken = page.token;
    } while (received < count);
    return measurements;
};

export const fetchLatestClinicalMeasurements = async ({
    patient,
    types,
    signal,
    source = 'DOCTOR'
}) => {
    const latest = await Promise.all(
        types.map(async (type) => {
            const page = await fetchClinicalMeasurementsPage({
                patient,
                type,
                pageLimit: 1,
                source,
                signal
            });
            if (page.count > 0 && !page.measurements.length) {
                throw new Error('Incomplete latest measurement response.');
            }
            return [type, page.measurements[0] || null];
        })
    );
    return Object.fromEntries(latest);
};

export const createClinicalMeasurement = async ({ patient, ts, dtype, data }) => {
    if (
        !patient ||
        !dtype ||
        !Number.isFinite(Date.parse(ts)) ||
        !data ||
        Array.isArray(data) ||
        typeof data !== 'object'
    ) {
        throw new Error('Invalid clinical measurement payload.');
    }
    await api.post('/cheart/measurements/v2', {
        patient,
        ts,
        dtype,
        source: 'DOCTOR',
        data
    });
};

export const clinicalMeasurementsToRows = (measurements, definition) =>
    measurements.map((measurement) => ({
        id: measurement.uuid,
        ts: measurement.ts,
        author: measurement.author,
        notes: measurement.data?.notes ?? null,
        ...Object.fromEntries(
            definition.fields.map(({ field }) => [
                field,
                measurement.data?.[field] ?? null
            ])
        )
    }));
